import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { decompressJson } from '@/utils/Utils.js'
import { logger } from '@/utils/logger'

const MAX_DAYFORCE_USERS_ACCESS_SYNC_CALLS_PER_DAY = 10

export async function sync({ boardId, processId }) {
  // ref: firebase function googleSheetIntegration-sync
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/${boardId}/sync`,
      { data: { processId } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) return ret.data // { status, msg }
    throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return error.response?.data
  }
}

/**
 * Returns the google_sheet_import_mapping entry for a boardId
 */
export async function getMapping({ boardId }) {
  try {
    const ret = await axios.get(`${await API_URL()}/import-mapping/${boardId}`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else if (ret.status === 204) {
      return null
    }

    throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const updateIntegrationImportMapping = async ({ boardId, type, autoSync }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/${boardId}/update-integration`,
      { data: { type, autoSync } },
      { headers: { ...(await getAuthHeader()) } }
    )
    if (ret.status === 200) return ret.data
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Returns the integration_import_mapping entry for a boardId
 */
export async function getIntegrationMapping({ boardId, type }) {
  try {
    const ret = await axios.get(`${await API_URL()}/import-mapping/${boardId}/integration`, {
      headers: { ...(await getAuthHeader()) },
      params: { type }
    })

    if (ret.status === 200) {
      return ret.data
    } else if (ret.status === 204) {
      return null
    }

    throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Updates the google_sheet_import_mapping table with the new mapping and filePath of a board
 */
export async function saveMapping({ boardId, mapping, filePath }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/${boardId}`,
      { data: { mapping, filePath } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) return ret.data // success
    throw new Error(ret)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Updates the google_sheet_import_mapping table with the new mapping and filePath of a board
 */
export async function saveIntegrationMapping({ boardId, type, config, mapping, autoSync }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/${boardId}/integration`,
      { data: { type, config, mapping, autoSync } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) return ret.data // success
    throw new Error(ret)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export async function createMapping({ boardId, mapping, filePath }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/${boardId}/create`,
      { data: { mapping, filePath } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) return ret.data // success
    throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return error.statusText
  }
}

export async function importGoogleSheet({ boardId, link }) {
  // ref: firebase function googleSheetIntegration-import
  logger.info('importGoogleSheet')
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/${boardId}/import-google-sheet`,
      { data: { link } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) return decompressJson(ret.data || {}) // The sheet's title, header, imported data, and importId
    throw new Error(ret.response)
  } catch (error) {
    logger.info(error)

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Request failed'
    throw new Error(errorMessage)
  }
}

export async function copyGoogleSheet({ sourceId, title }) {
  // ref: firebase function googleSheetIntegration-createCopy
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/create-copy`,
      { data: { sourceId, title } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) return ret.data // docId of new doc
    throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export async function updateGoogleSheet({ docId, props }) {
  // ref: firebase function googleSheetIntegration-updateDoc
  const updateDoc = async (docId, props) => {
    try {
      const ret = await axios.post(
        `${await API_URL()}/import-mapping/update-doc`,
        { data: { docId, props } },
        { headers: { ...(await getAuthHeader()) } }
      )

      if (ret.status === 200) return ret.data // success
      throw new Error(ret.statusText)
    } catch (error) {
      logger.info(error)
      return null
    }
  }

  let res
  try {
    res = await updateDoc({ docId, props })
  } catch (e) {
    logger.info('err caught in updateDoc')
    let retryCount = 0
    let isDone = false
    while (!isDone && retryCount < 11) {
      try {
        retryCount++
        res = await updateDoc({ docId, props })
        isDone = true
      } catch (err) {
        logger.info('Retrying document update', retryCount, err)
      }
    }
  }
  return res
}

export async function updateAccessGoogleSheet({ docId, email }) {
  // ref: firebase function googleSheetIntegration-givePermissionForUser
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/give-permission`,
      { data: { docId, email } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return ret.data // success
    }

    throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export async function uploadCSV({
  boardId,
  formData = null,
  bucketFilePath = null,
  processId = null,
  integrationType = null,
  replaceExistingData = true,
  mapping = null
}) {
  // Sanity check - Either upload the csv file here or pass the path to the bucket where the file was uploaded
  if ((!formData && !bucketFilePath) || (formData && bucketFilePath)) {
    throw new Error('Error uploading CSV - both formData and bucketFilePath are used')
  }

  try {
    let ret = {}
    if (bucketFilePath) {
      ret = await axios.post(
        `${await API_URL()}/import-mapping/${boardId}/import-csv?replaceExistingData=${replaceExistingData}`,
        { data: { bucketFilePath, integrationType, mapping } },
        {
          headers: { ...(await getAuthHeader()) },
          params: { processId },
          timeout: 0
        }
      )
    } else {
      ret = await axios.post(
        `${await API_URL()}/import-mapping/${boardId}/import-csv?replaceExistingData=${replaceExistingData}`,
        formData,
        {
          headers: { ...(await getAuthHeader()), 'Content-Type': 'multipart/form-data' },
          params: { processId },
          timeout: 0
        }
      )
    }

    if (ret.status === 200) return ret.data // The sheet's title, header, imported data, and importId
    throw new Error(ret.response)
  } catch (error) {
    logger.info(error)
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Upload failed'
    throw new Error(errorMessage)
  }
}

export async function uploadSalaryMapping({ boardId, formData = null, bucketFilePath = null }) {
  // ref: firebase function googleSheetIntegration-import
  logger.info('importCSV')

  // Sanity check - Either upload the csv file here or pass the path to the bucket where the file was uploaded
  if ((!formData && !bucketFilePath) || (formData && bucketFilePath)) {
    throw new Error('Error uploading CSV - both formData and bucketFilePath are used')
  }

  try {
    let ret = {}

    ret = await axios.post(`${await API_URL()}/settings/${boardId}/salary-mapping`, formData, {
      headers: { ...(await getAuthHeader()), 'Content-Type': 'multipart/form-data' }
    })

    if (ret.status === 200) return ret.data // The sheet's title, header, imported data, and importId
    throw new Error(ret.response)
  } catch (error) {
    logger.info(error)
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Upload failed'
    throw new Error(errorMessage)
  }
}

export async function uploadRateCardMapping({ boardId, formData }) {
  if (!formData) {
    throw new Error('Error uploading rate card mapping - formData is required')
  }

  try {
    const { data } = await axios.post(
      `${await API_URL()}/settings/${boardId}/rate-card-mapping`,
      formData,
      {
        headers: {
          ...(await getAuthHeader()),
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return data
  } catch (error) {
    logger.error('Error uploading rate card mapping:', error.message)
    throw error.response?.data || error
  }
}

export async function uploadFieldsMapping({ boardId, formData }) {
  if (!formData) {
    throw new Error('Error uploading fields mapping - formData is required')
  }

  try {
    const { data } = await axios.post(
      `${await API_URL()}/settings/${boardId}/fields-mapping`,
      formData,
      {
        headers: {
          ...(await getAuthHeader()),
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return data
  } catch (error) {
    logger.error('Error uploading fields mapping:', error.message)
    throw error.response?.data || error
  }
}

export async function uploadJobCatalogMapping({ boardId, formData }) {
  if (!formData) {
    throw new Error('Error uploading job catalog mapping - formData is required')
  }

  try {
    const { data } = await axios.post(
      `${await API_URL()}/settings/${boardId}/job-catalog-mapping`,
      formData,
      {
        headers: {
          ...(await getAuthHeader()),
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return data
  } catch (error) {
    logger.error('Error uploading job catalog mapping:', error.message)
    throw error.response?.data || error
  }
}

export async function uploadSkillsCatalog({ boardId, formData = null, bucketFilePath = null }) {
  // ref: firebase function googleSheetIntegration-import
  logger.info('importCSV')

  // Sanity check - Either upload the csv file here or pass the path to the bucket where the file was uploaded
  if ((!formData && !bucketFilePath) || (formData && bucketFilePath)) {
    throw new Error('Error uploading CSV - both formData and bucketFilePath are used')
  }

  try {
    let ret = {}

    ret = await axios.post(`${await API_URL()}/settings/${boardId}/skills-catalog`, formData, {
      headers: { ...(await getAuthHeader()), 'Content-Type': 'multipart/form-data' }
    })

    if (ret.status === 200) return ret.data // The sheet's title, header, imported data, and importId
    throw new Error(ret.response)
  } catch (error) {
    logger.info(error)
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Upload failed'
    throw new Error(errorMessage)
  }
}

/**
 * Start a Dayforce refresh for a board.
 *
 * A 409 means the board is already refreshing, which is not an error for the caller: the backend
 * returns the running attempt's processId, so waiting on it attaches to that run instead of
 * starting a competing one.
 *
 * @param {object} params - Parameters
 * @param {string} params.boardId - The main board ID
 * @param {string} params.namespace - The Dayforce namespace
 * @returns {Promise<{processId: string}>} The attempt to wait on
 */
export async function fetchDayforceData({ boardId, namespace }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/${boardId}/import-dayforce`,
      { data: { namespace } },
      { headers: { ...(await getAuthHeader()) } }
    )
    logger.info(ret)
    if (ret.status === 200) return ret.data
    throw new Error(ret.response)
  } catch (error) {
    logger.info(error)

    if (error.response?.status === 409 && error.response.data?.processId) {
      logger.info('Dayforce sync already running, attaching to it', error.response.data.processId)
      return error.response.data
    }

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Request failed'
    throw new Error(errorMessage)
  }
}

/**
 * Find a board by namespace and add the current user with no_access.
 * Used for Dayforce embedded users who need to join an existing workspace.
 * @param {string} namespace - The Dayforce namespace to look up
 * @returns {Promise<{boardId: string|null}>} The boardId if found and user added, null if not found
 */
export async function joinBoardByNamespace({ namespace }) {
  logger.info('[joinBoardByNamespace] Calling API with namespace:', namespace)
  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/join-by-namespace`,
      { data: { namespace } },
      { headers: { ...(await getAuthHeader()) } }
    )
    logger.info('[joinBoardByNamespace] Response:', ret.status, ret.data)
    if (ret.status === 200) return ret.data
    throw new Error(ret.response)
  } catch (error) {
    logger.error(
      '[joinBoardByNamespace] Error:',
      error.response?.status,
      error.response?.data || error.message
    )
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.response?.data ||
      error.message ||
      'Request failed'
    throw new Error(errorMessage)
  }
}

/**
 * Sync dayforce users into board owners/access levels and run dayforce userId -> uid migrations.
 *
 * @param {string} boardId - The boardId to sync dayforce users for
 * @returns {Boolean} - True if the sync was successful, false otherwise
 */
export const syncDayforceUsersAccessForBoard = async ({ boardId }) => {
  if (!boardId) {
    throw new Error('boardId is required')
  }

  if (!throttleDayforceUsersAccessSync(boardId)) {
    logger.warn('Skipping dayforce users access sync - daily limit reached', {
      boardId,
      limit: MAX_DAYFORCE_USERS_ACCESS_SYNC_CALLS_PER_DAY
    })
    return { success: true, throttled: true }
  }

  try {
    const ret = await axios.post(
      `${await API_URL()}/import-mapping/${boardId}/sync-dayforce-users-access`,
      { data: {} },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return ret.data
    }
    throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Failed to sync dayforce users access for board', {
      error: error.message,
      boardId
    })
    throw error
  }
}

/**
 * Throttle requests to dayforce users access sync
 *
 * @param {string} boardId - The boardId to throttle dayforce users access sync for
 * @returns {Boolean} - True if the throttle is not exceeded, false otherwise
 */
const throttleDayforceUsersAccessSync = (boardId) => {
  if (typeof localStorage === 'undefined') {
    return true
  }

  const today = new Date().toISOString().slice(0, 10)

  try {
    const key = `import-api:dayforce-users-access-sync:${boardId || 'board'}`
    const raw = localStorage.getItem(key)
    const state = raw ? JSON.parse(raw) : null
    const count = state?.date === today ? state.count || 0 : 0

    if (count >= MAX_DAYFORCE_USERS_ACCESS_SYNC_CALLS_PER_DAY) {
      return false
    }

    localStorage.setItem(
      key,
      JSON.stringify({
        date: today,
        count: count + 1
      })
    )

    return true
  } catch (error) {
    logger.warn('Failed to read/save dayforce users access sync throttle state', {
      boardId,
      error: error.message
    })
    return true
  }
}

/**
 * Upload CSV using the new dedicated partial upload endpoint
 * @param {Object} params - Upload parameters
 * @param {string} params.boardId - Board ID
 * @param {FormData} params.formData - Form data containing file and mapping
 * @param {string} params.processId - Process ID for tracking
 * @returns {Promise} Upload result
 */
export async function uploadPartialCSV({ boardId, formData, processId = null }) {
  if (!formData) {
    throw new Error('Error uploading partial CSV - formData is required')
  }

  try {
    const ret = await axios.post(`${await API_URL()}/partial-upload/${boardId}/csv`, formData, {
      headers: {
        ...(await getAuthHeader()),
        'Content-Type': 'multipart/form-data'
      },
      params: { processId },
      timeout: 0
    })

    if (ret.status === 200) return ret.data
    throw new Error(ret.response)
  } catch (error) {
    logger.info('Partial upload error:', error)
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Upload failed'
    throw new Error(errorMessage)
  }
}
